import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { formatHours, percentOf, SERIES } from '../charts/chart-theme';
import { StackedBarChart } from '../charts/grouped-bar-chart';
import { MultiLineTrend } from '../charts/multi-line-trend';
import { ChartTableToggle, type ChartTableView, SegmentedToggle } from '../segmented-toggle';
import { WidgetCard, type WidgetInsights } from '../widget-card';
import { WidgetLegend } from '../widget-legend';
import { type WidgetColumn, WidgetTable } from '../widget-table';
import {
  ALL_PEOPLE,
  aboveLevelHours,
  belowLevelHours,
  LOCATIONS,
  MISMATCH_TREND,
  MONTHS,
  type Person,
  ROLES,
  SEGMENTS,
} from './fixtures';

const LEGEND = [
  { color: SERIES.idle, label: 'Booked below level' },
  { color: SERIES.nonBillable, label: 'Booked above level' },
];

/** How the exceptions are cut up. */
type Grouping = 'role' | 'location' | 'segment';

const GROUP_NOUN: Record<Grouping, string> = {
  role: 'Designated role',
  location: 'Location',
  segment: 'Segment',
};

interface GroupSummary {
  /** Bucket name: a role code, a location, or a segment. */
  name: string;
  below: number;
  above: number;
  offLevel: number;
  /** All committed hours in this bucket — the denominator for the share. */
  totalHours: number;
  people: number;
  peopleOffLevel: number;
  /** The people behind this bucket that actually have off-level hours. */
  offLevelPeople: Person[];
}

function summarize(name: string, people: Person[]): GroupSummary {
  const below = belowLevelHours(people);
  const above = aboveLevelHours(people);
  return {
    name,
    below,
    above,
    offLevel: below + above,
    totalHours: people.reduce((sum, p) => sum + p.committed, 0),
    people: people.length,
    peopleOffLevel: people.filter((p) => p.mismatchHours > 0).length,
    offLevelPeople: people.filter((p) => p.mismatchHours > 0),
  };
}

/**
 * Exceptions only: hours booked at a level other than the one the person is
 * employed at.
 *
 * At-level hours are excluded on purpose — they're the overwhelming majority
 * and would flatten the exceptions into invisibility.
 *
 * The grouping switcher mirrors the engagement tiles. By **role** the x-axis is
 * the level the person is employed at, so a tall bar means "this grade's time
 * is going somewhere else". By **location** or **segment** the same exception
 * hours are re-cut to show *where* the mismatch is concentrated, which is the
 * staffing question. The 12-month trend is firm-level, so it only appears in
 * the role view — as with the engagement tiles.
 */
export function RoleMismatchTile({
  insights,
  onDrillGroup,
  onDrillAll,
}: {
  insights: WidgetInsights;
  onDrillGroup: (label: string, people: Person[]) => void;
  onDrillAll: (people: Person[]) => void;
}) {
  const [view, setView] = useState<ChartTableView>('chart');
  const [grouping, setGrouping] = useState<Grouping>('role');

  const groups = useMemo<GroupSummary[]>(() => {
    if (grouping === 'location') {
      return LOCATIONS.map((l) =>
        summarize(
          l.name,
          ALL_PEOPLE.filter((p) => p.location === l.name),
        ),
      );
    }
    if (grouping === 'segment') {
      return SEGMENTS.map((s) =>
        summarize(
          s.name,
          ALL_PEOPLE.filter((p) => p.segment === s.name),
        ),
      );
    }
    return ROLES.map((role) =>
      summarize(
        role,
        ALL_PEOPLE.filter((p) => p.designatedRole === role),
      ),
    );
  }, [grouping]);

  const firm = useMemo(() => {
    const below = belowLevelHours(ALL_PEOPLE);
    const above = aboveLevelHours(ALL_PEOPLE);
    return {
      below,
      above,
      offLevel: below + above,
      offLevelPeople: ALL_PEOPLE.filter((p) => p.mismatchHours > 0),
      totalCommitted: ALL_PEOPLE.reduce((sum, p) => sum + p.committed, 0),
    };
  }, []);

  // Memoised for the same reason as MixTile's — see WidgetTable.
  const columns: WidgetColumn<GroupSummary>[] = useMemo(
    () => [
      {
        key: 'name',
        header: GROUP_NOUN[grouping],
        render: (g) => (grouping === 'role' ? <Badge variant="outline">{g.name}</Badge> : g.name),
        sortValue: (g) => g.name,
      },
      {
        key: 'people',
        header: 'People',
        numeric: true,
        render: (g) => g.people,
        sortValue: (g) => g.people,
      },
      {
        key: 'peopleOffLevel',
        header: 'With mismatch',
        numeric: true,
        render: (g) => g.peopleOffLevel,
        sortValue: (g) => g.peopleOffLevel,
      },
      {
        key: 'below',
        header: 'Below level hrs',
        numeric: true,
        render: (g) => (g.below ? formatHours(g.below) : '—'),
        sortValue: (g) => g.below,
      },
      {
        key: 'above',
        header: 'Above level hrs',
        numeric: true,
        render: (g) => (g.above ? formatHours(g.above) : '—'),
        sortValue: (g) => g.above,
      },
      {
        key: 'offLevel',
        header: 'Total off-level',
        numeric: true,
        render: (g) => formatHours(g.offLevel),
        sortValue: (g) => g.offLevel,
      },
      {
        key: 'share',
        header: `% of ${grouping} hrs`,
        numeric: true,
        render: (g) => <b>{percentOf(g.offLevel, g.totalHours)}%</b>,
        sortValue: (g) => g.offLevel / (g.totalHours || 1),
      },
    ],
    [grouping],
  );

  return (
    <WidgetCard
      title="Role Mismatch — exceptions only"
      subtitle={`${formatHours(firm.offLevel)} hrs booked away from the person's designated level (${percentOf(firm.offLevel, firm.totalCommitted)}% of committed) · ${firm.offLevelPeople.length} people · at-level hours excluded · click to drill`}
      controls={
        <>
          <SegmentedToggle
            label="Group by"
            value={grouping}
            onChange={setGrouping}
            options={[
              { value: 'role', label: 'Role' },
              { value: 'location', label: 'Location' },
              { value: 'segment', label: 'Segment' },
            ]}
          />
          <ChartTableToggle value={view} onChange={setView} />
        </>
      }
      insights={insights}
    >
      {view === 'chart' ? (
        <>
          <StackedBarChart
            height={grouping === 'role' ? 190 : 230}
            onBarClick={(i) => {
              const group = groups[i];
              if (group) onDrillGroup(group.name, group.offLevelPeople);
            }}
            bars={groups.map((g) => ({
              label: g.name,
              display: g.offLevel ? `${formatHours(g.offLevel)} hrs` : '',
              segments: [
                { key: 'below', label: 'Booked below level', value: g.below, color: SERIES.idle },
                {
                  key: 'above',
                  label: 'Booked above level',
                  value: g.above,
                  color: SERIES.nonBillable,
                },
              ],
            }))}
          />
          {/* Firm-level history — it doesn't vary by grouping, so it would be
              misleading beside per-location or per-segment bars. */}
          {grouping === 'role' ? (
            <div className="mt-2 border-t pt-2">
              <MultiLineTrend
                height={110}
                labels={MONTHS}
                series={[
                  {
                    key: 'below',
                    label: 'Below level',
                    color: SERIES.idle,
                    values: MISMATCH_TREND.map((m) => m.below),
                  },
                  {
                    key: 'above',
                    label: 'Above level',
                    color: SERIES.nonBillable,
                    values: MISMATCH_TREND.map((m) => m.above),
                  },
                ]}
              />
            </div>
          ) : null}
        </>
      ) : (
        <WidgetTable
          rows={groups}
          columns={columns}
          defaultSortKey="offLevel"
          onRowClick={(g) => onDrillGroup(g.name, g.offLevelPeople)}
        />
      )}
      <WidgetLegend
        items={LEGEND}
        note={
          <button
            type="button"
            className="underline underline-offset-2 hover:text-foreground"
            onClick={() => onDrillAll(firm.offLevelPeople)}
          >
            drill all {firm.offLevelPeople.length} people
          </button>
        }
      />
    </WidgetCard>
  );
}
